type WidgetTextFn = (...path: string[]) => string

type UsageDisplayItem = {
  type: string
  typeCounter?: number
  buildingTypeId?: string | null
  buildingTypeName?: string | null
}

type UsageDisplayContext = {
  allUsages?: UsageDisplayItem[]
  index?: number
  resolveBuildingTypeName?: (buildingTypeId?: string | null) => string | null
}

function getResolvedBuildingTypeName(
  usage: UsageDisplayItem,
  resolveBuildingTypeName?: (buildingTypeId?: string | null) => string | null
): string | null {
  const buildingTypeId = usage.buildingTypeId?.trim()
  if (buildingTypeId && resolveBuildingTypeName) {
    const resolvedName = resolveBuildingTypeName(buildingTypeId)?.trim()
    if (resolvedName) return resolvedName
  }

  const buildingTypeName = usage.buildingTypeName?.trim()
  return buildingTypeName || null
}

function getBuildingTypeKey(
  usage: UsageDisplayItem,
  resolveBuildingTypeName?: (buildingTypeId?: string | null) => string | null
): string | null {
  const buildingTypeId = usage.buildingTypeId?.trim()
  if (buildingTypeId) return `id:${buildingTypeId}`

  const buildingTypeName = getResolvedBuildingTypeName(usage, resolveBuildingTypeName)
  if (buildingTypeName) return `name:${buildingTypeName}`

  return null
}

export function getUsageDisplayTitle(usage: UsageDisplayItem, wt: WidgetTextFn, context?: UsageDisplayContext): string {
  const allUsages = context?.allUsages
  const index = context?.index
  const resolveBuildingTypeName = context?.resolveBuildingTypeName
  const buildingTypeName = getResolvedBuildingTypeName(usage, resolveBuildingTypeName)
  if (buildingTypeName) {
    const buildingTypeKey = getBuildingTypeKey(usage, resolveBuildingTypeName)

    if (buildingTypeKey && allUsages && typeof index === 'number' && index >= 0) {
      const sameComboBeforeCount = allUsages
        .slice(0, index)
        .filter(
          (candidate) =>
            candidate.type === usage.type && getBuildingTypeKey(candidate, resolveBuildingTypeName) === buildingTypeKey
        ).length

      if (sameComboBeforeCount > 0) {
        return `${buildingTypeName} ${sameComboBeforeCount + 1}`
      }
    }

    return buildingTypeName
  }

  let title = wt('usages', 'option', usage.type)
  if (allUsages && typeof index === 'number' && index >= 0) {
    const sameUsageWithoutBuildingTypeBeforeCount = allUsages
      .slice(0, index)
      .filter(
        (candidate) =>
          candidate.type === usage.type && !candidate.buildingTypeId?.trim() && !candidate.buildingTypeName?.trim()
      ).length

    if (sameUsageWithoutBuildingTypeBeforeCount > 0) {
      return `${title} ${sameUsageWithoutBuildingTypeBeforeCount + 1}`
    }
    return title
  }

  if (usage.typeCounter && usage.typeCounter > 0 && !usage.buildingTypeId?.trim() && !usage.buildingTypeName?.trim()) {
    title += ` ${usage.typeCounter + 1}`
  }
  return title
}

export function getUsageDisplaySubtitle(
  usage: UsageDisplayItem,
  wt: WidgetTextFn,
  resolveBuildingTypeName?: (buildingTypeId?: string | null) => string | null
): string {
  const buildingTypeName = getResolvedBuildingTypeName(usage, resolveBuildingTypeName)
  if (!buildingTypeName) {
    return ''
  }
  return wt('usages', 'option', usage.type)
}
